/* eslint-disable prettier/prettier */
import { get, post, del } from '@/infra/rest';
import {
  SendThirdPartyVendorEmailPayload,
  VendorIntakeFormData,
} from '@/modules/agent/types/vendorIntakeForm';
import { APIEndpoints } from '@/shared/constants/Endpoints';
import { QueryKeys } from '@/shared/constants/QueryKeys';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AllVendorsListResponse, VendorDetails } from '../types';

export const getVendorsList = async (): Promise<AllVendorsListResponse> => {
  const response = await get<AllVendorsListResponse>({
    url: APIEndpoints.FetchVendorsList,
  });
  return response;
};

const getVendorRiskLevel = (risk: string) => {
  switch (risk) {
    case 'Low':
      return 1;
    case 'Medium':
      return 2;
    case 'High':
      return 3;
    default:
      return 4;
  }
};

const getVendorStatusLevel = (risk: string) => {
  switch (risk) {
    case 'pending_review':
      return 1;
    case 'in_review':
      return 2;
    case 'reviewed':
      return 3;
    default:
      return 4;
  }
};

export const useGetVendorsListQuery = () => {
  return useQuery<VendorDetails[]>({
    queryKey: [QueryKeys.AllVendorsList],
    queryFn: async () => {
      const response = await getVendorsList();
      return response.data
        .map((vendorDetails, vendorIndex) => ({
          ...vendorDetails,
          vendor_product_name: vendorDetails.vendor_name.concat(
            ' ',
            vendorDetails.product_name
          ),
          uid: `vendor-${vendorIndex}`,
          risk_level: getVendorRiskLevel(vendorDetails.risk),
          status_level: getVendorStatusLevel(vendorDetails.status),
        }))
        .sort((a, b) => {
          const aTime = dayjs(a.last_assessed).isValid()
            ? dayjs(a.last_assessed).valueOf()
            : 0;
          const bTime = dayjs(b.last_assessed).isValid()
            ? dayjs(b.last_assessed).valueOf()
            : 0;
          return bTime - aTime;
        });
    },
  });
};

export const validateThirdPartyVendorFormToken = async (
  token: string
): Promise<any> => {
  try {
    const response = await get<any>({
      url: APIEndpoints.ValidateThirdPartyVendorToken,
      config: {
        params: {
          token,
        },
        headers: {
          token,
        },
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const submitThridPartyVendorIntakeForm = async (
  payload: VendorIntakeFormData
): Promise<any> => {
  const { token, ...data } = payload;
  const response = await post({
    url: APIEndpoints.SubmitThirdPartyVendorForm,
    data,
    config: {
      headers: {
        token,
      },
    },
  });
  return response;
};

export const useGetThirdPartyFormDetailsQuery = (token: string | null) => {
  return useQuery<any>({
    queryKey: [QueryKeys.ThirdPartyVendorToken, token],
    queryFn: async () => {
      if (token) {
        const response = await validateThirdPartyVendorFormToken(token);

        return response;
      } else {
        throw new Error('Invalid Token');
      }
    },
    enabled: !!token,
    retry: false,
  });
};

export const useSubmitVendorIntakeForm = () => {
  return useMutation({
    mutationFn: submitThridPartyVendorIntakeForm,
  });
};

export const sendThirdPartyVendorFormEmail = async (
  data: SendThirdPartyVendorEmailPayload
) => {
  const response = await post({
    url: APIEndpoints.SendThirdPartyVendorFormEmail,
    data,
  });
  return response;
};

export const useSendThirdPartyVendorFormEmail = () => {
  return useMutation({
    mutationFn: sendThirdPartyVendorFormEmail,
  });
};

// Define response types
type VendorIntakeFormLinkResponse = {
  success: boolean;
  message: string;
  data: {
    vendor_intake_form_url: string;
    expires_in: string;
  };
};

type DiscardAgentSessionResponse = {
  success: boolean;
  message: string;
  data: {
    agent_session_id: string;
  };
};

export const getVendorIntakeFormLink = async (data: {
  vendor_email: string;
  agent_session_id: string;
}): Promise<VendorIntakeFormLinkResponse> => {
  const response = await post({
    url: '/v0/rag/workflows/multi_file_va/vendor/intake-form-link',
    data,
  });
  return response as VendorIntakeFormLinkResponse;
};

export const useGetVendorIntakeFormLink = () => {
  return useMutation({
    mutationFn: getVendorIntakeFormLink,
  });
};

export const discardAgentSession = async (agentSessionId: string): Promise<DiscardAgentSessionResponse> => {
  const response = await del({
    url: `/v0/rag/workflows/multi_file_va/discard-session/${agentSessionId}`,
  });
  return response as DiscardAgentSessionResponse;
};

export const useDiscardAgentSession = () => {
  return useMutation({
    mutationFn: discardAgentSession,
  });
};
